import { useEffect, useRef, useCallback } from "react";
import AppStoreBadge from "./app-store-badge.svg";
import { AnimatedBird } from "./AnimatedBird";
import { BirdSitting } from "./BirdSitting";

const APP_STORE_URL =
  "https://apps.apple.com/us/app/pip-baby-tracker/id6740407132";

function useFadeIn() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("visible");
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
}

function FadeIn({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useFadeIn();
  return (
    <div ref={ref} className={`fade-in ${className}`}>
      {children}
    </div>
  );
}

const FEATURES = {
  Feeding: [
    { name: "Nursing", color: "#E88B9C" },
    { name: "Bottle", color: "#DC3E42" },
    { name: "Pumping", color: "#E76F51" },
    { name: "Solids", color: "#F4A261" },
  ],
  Sleep: [{ name: "Sleep Timer", color: "#7B68EE" }],
  Diapers: [{ name: "Diaper", color: "#4ECDC4" }],
  Activities: [
    { name: "Tummy Time", color: "#82AF56" },
    { name: "Bath", color: "#0596A6" },
    { name: "Walking", color: "#169944" },
    { name: "Crying", color: "#5151CD" },
  ],
  Health: [
    { name: "Temperature", color: "#E12548" },
    { name: "Medication", color: "#9339EF" },
    { name: "Vaccination", color: "#0084C7" },
  ],
  Growth: [
    { name: "Weight", color: "#0084C7" },
    { name: "Height", color: "#0596A6" },
    { name: "Head Circumference", color: "#6C7E8E" },
  ],
  Memories: [
    { name: "Moments", color: "#CFB47D" },
    { name: "Milestones", color: "#9A7A48" },
  ],
};

export default function LandingPage() {
  useEffect(() => {
    document.title = "Pip Baby Tracker";
  }, []);

  const scrollToFeatures = useCallback(() => {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  }, []);

  return (
    <>
      {/* ===== Hero ===== */}
      <section className="hero">
        <div className="hero-content">
          <AnimatedBird />
          <div className="hero-tagline">
            baby
            <br />
            tracker
          </div>
          <p className="hero-description">
            Track feedings, diapers, sleep, and more &mdash; all in one beautifully
            simple app for parents and caregivers.
          </p>
          <div className="hero-cta">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="app-store-link"
            >
              <img
                src={AppStoreBadge}
                alt="Download on the App Store"
              />
            </a>
          </div>
        </div>
        <button
          className="hero-scroll-hint"
          onClick={scrollToFeatures}
          aria-label="Scroll to features"
          style={{ background: "none", border: "none", cursor: "pointer" }}
        >
          &#8595;
        </button>
      </section>

      {/* ===== Value Proposition ===== */}
      <section className="section value-prop" id="features">
        <div className="section-inner">
          <FadeIn>
            <h2 className="section-title center">
              Everything you need. Nothing you don't.
            </h2>
            <p className="section-subtitle center">
              Pip gives you a clear picture of your baby's day without the
              clutter. Built by parents, for parents.
            </p>
          </FadeIn>
          <div className="value-grid">
            <FadeIn>
              <div className="value-card">
                <span className="value-icon" role="img" aria-label="timer">
                  &#9201;
                </span>
                <h3>One-Tap Tracking</h3>
                <p>
                  Start a nursing, sleep, or tummy time timer with a single tap.
                  Log diapers and feeds in seconds.
                </p>
              </div>
            </FadeIn>
            <FadeIn>
              <div className="value-card">
                <span className="value-icon" role="img" aria-label="sync">
                  &#128260;
                </span>
                <h3>Real-Time Sync</h3>
                <p>
                  Both parents and caregivers see updates instantly. Everyone
                  stays on the same page, always.
                </p>
              </div>
            </FadeIn>
            <FadeIn>
              <div className="value-card">
                <span className="value-icon" role="img" aria-label="chart">
                  &#128200;
                </span>
                <h3>Beautiful Insights</h3>
                <p>
                  See daily summaries, weekly trends, and growth charts. Spot
                  patterns in sleep, feeding, and more.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ===== Features ===== */}
      <section className="section features">
        <div className="section-inner">
          <FadeIn>
            <h2 className="section-title">19 trackers. One app.</h2>
            <p className="section-subtitle">
              From the first feed to the first steps &mdash; Pip tracks every moment
              that matters.
            </p>
          </FadeIn>
          <div className="feature-categories">
            {Object.entries(FEATURES).map(([category, items]) => (
              <FadeIn key={category}>
                <div className="feature-category">
                  <span className="feature-category-title">{category}</span>
                  <div className="feature-list">
                    {items.map((item) => (
                      <div className="feature-item" key={item.name}>
                        <div
                          className="feature-dot"
                          style={{ backgroundColor: item.color }}
                        />
                        <span>{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Platforms ===== */}
      <section className="section platforms">
        <div className="section-inner">
          <FadeIn>
            <h2 className="section-title center">Everywhere you are.</h2>
            <p className="section-subtitle center">
              Track from your iPhone, Apple Watch, Lock Screen, or Home Screen.
              Pip is always within reach.
            </p>
          </FadeIn>
          <div className="platform-grid">
            <FadeIn>
              <div className="platform-card">
                <span className="platform-icon" role="img" aria-label="phone">
                  &#128241;
                </span>
                <h3>iPhone</h3>
                <p>
                  The full Pip experience with customizable home tiles,
                  detailed history, and beautiful insights.
                </p>
              </div>
            </FadeIn>
            <FadeIn>
              <div className="platform-card">
                <span className="platform-icon" role="img" aria-label="watch">
                  &#8986;
                </span>
                <h3>Apple Watch</h3>
                <p>
                  Quick-log feedings and start timers right from your wrist.
                  Perfect for middle-of-the-night feeds.
                </p>
              </div>
            </FadeIn>
            <FadeIn>
              <div className="platform-card">
                <span className="platform-icon" role="img" aria-label="widget">
                  &#128274;
                </span>
                <h3>Lock Screen & Widgets</h3>
                <p>
                  Live Activities show running timers on your Lock Screen and
                  Dynamic Island. Home widgets for at-a-glance stats.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ===== Caregiver Sync ===== */}
      <section className="section sync">
        <div className="section-inner">
          <FadeIn>
            <h2 className="section-title">Your whole care team, in sync.</h2>
            <p className="section-subtitle">
              Invite partners, grandparents, nannies &mdash; anyone helping care for
              your little one.
            </p>
          </FadeIn>
          <div className="sync-content">
            <div className="sync-text">
              <FadeIn>
                <div className="sync-features">
                  <div className="sync-feature">
                    <div className="sync-check">&#10003;</div>
                    Invite caregivers with a simple link
                  </div>
                  <div className="sync-feature">
                    <div className="sync-check">&#10003;</div>
                    Real-time updates across all devices
                  </div>
                  <div className="sync-feature">
                    <div className="sync-check">&#10003;</div>
                    Track multiple babies
                  </div>
                  <div className="sync-feature">
                    <div className="sync-check">&#10003;</div>
                    Everyone sees the same data, instantly
                  </div>
                  <div className="sync-feature">
                    <div className="sync-check">&#10003;</div>
                    Push notifications for timer events
                  </div>
                </div>
              </FadeIn>
            </div>
            <div className="sync-visual">
              <FadeIn>
                <BirdSitting />
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="section cta">
        <FadeIn>
          <h2 className="section-title center">
            Start tracking with Pip today.
          </h2>
          <p className="section-subtitle center">
            Free to download. Effortless to use. Built with love.
          </p>
          <div className="hero-cta">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="app-store-link"
            >
              <img
                src={AppStoreBadge}
                alt="Download on the App Store"
              />
            </a>
          </div>
        </FadeIn>
      </section>

      {/* ===== Footer ===== */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <span className="footer-name">pip</span>
          </div>
          <div className="footer-links">
            <a href="/privacy">Privacy Policy</a>
            <a href="/terms">Terms of Service</a>
            <a href="mailto:support@pipbabytracker.com">Contact</a>
          </div>
          <span className="footer-copy">
            &copy; {new Date().getFullYear()} Pip Baby Tracker
          </span>
        </div>
      </footer>
    </>
  );
}
